import React, { useState, useEffect } from 'react';
import { Box, Button, TextField, MenuItem } from '@mui/material';
import { Droppable, Draggable } from 'react-beautiful-dnd';
import { fieldTypes } from '../../../config/fieldTypesConfig';
import { fieldSelector } from '../../../utils/fieldSelector';

const CustomNoteComponent = ({ properties = {}, id, valueUpdaterCallback, validityUpdaterCallback, setParentDragDisabled, value }) => {
    const { constrains = {} } = properties; // Ensure default values for properties
    const mode = properties.mode || 'design';
    
    // Initialize state - if value is null/undefined, start with empty array
    const [fields, setFieldsInternal] = useState(() => {
        console.log("CustomNote initial value:", value);
        return (value && Array.isArray(value)) ? value : [];
    });
    
    const setFields = (updatedFields) => {
        console.log("setFields called with:", updatedFields);
        setFieldsInternal(updatedFields);
        if (valueUpdaterCallback) {
            console.log("Calling valueUpdaterCallback with:", updatedFields);
            valueUpdaterCallback(updatedFields); // Notify parent about the updated fields
        }
    };

    // Initialize with empty array on mount if value is null
    useEffect(() => {
        console.log("CustomNote mounted, value:", value);
        if (valueUpdaterCallback && (value === null || value === undefined)) {
            console.log("Initializing CustomNote value with empty array");
            valueUpdaterCallback([]);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Sync with parent value changes (for drag-and-drop reordering)
    useEffect(() => {
        console.log("CustomNote value changed:", value);
        if (value && Array.isArray(value) && value.length > 0) {
            console.log("CustomNote syncing with parent value:", value);
            setFieldsInternal(value);
        }
    }, [value]);

    const [nextFieldId, setNextFieldId] = useState(0);
    const [fieldType, setFieldType] = useState('');
    const [fieldName, setFieldName] = useState('');

    // Helper functions to retrieve and update fields
    const get_item_by_id = (id) => fields.find((field) => field.id === id);
    const get_item_index_by_id = (id) => fields.findIndex((field) => field.id === id);

    const handleAddField = () => {
        console.log("handleAddField called");
        console.log("Current fieldType:", fieldType);
        console.log("Current fieldName:", fieldName);

        if (!fieldType || !fieldName) {
            console.warn("Field type or name is missing");
            return;
        }

        if (constrains?.maxFields && fields.length >= constrains?.maxFields) {
            console.error("Maximum number of fields reached.");
            return;
        }
        let this_field_id = nextFieldId;
        console.log(`custom-note-field-${id}-${this_field_id}`)
        setNextFieldId(nextFieldId + 1);
        const newField = {
            id: `custom-note-field-${id}-${this_field_id}`, // Ensure unique IDs
            type: fieldType,
            name: fieldName,
            properties: { options: {}, constrains: {} }, // Ensure properties are defined
            value: null,
            valid: false,
        };
        console.log("Adding new field:", newField);
        setFields([...fields, newField]);
        setFieldType('');
        setFieldName('');
    };

    const handleRemoveField = (id) => {
        console.log("handleRemoveField called for field ID:", id);
        setFields(fields.filter((field) => field.id !== id));
    };

    const handleBeforeDragStart = () => {
        console.log("Drag start in CustomNoteComponent - disabling parent drag");
        if (setParentDragDisabled) {
            setParentDragDisabled(true); // Disable parent dragging
        }
    };

    const handleAfterDragEnd = () => {
        console.log("Drag end in CustomNoteComponent - enabling parent drag");
        if (setParentDragDisabled) {
            setParentDragDisabled(false); // Re-enable parent dragging
        }
    };

    return (
        <Box sx={{ width: '100%', maxWidth: '100%' }}>
            {mode === 'design' && (
                <Box 
                    display="flex" 
                    flexDirection={{ xs: 'column', sm: 'row' }} 
                    alignItems={{ xs: 'stretch', sm: 'center' }} 
                    gap={{ xs: 1.5, sm: 1 }} 
                    mb={2}
                >
                    <TextField
                        select
                        label="Field Type"
                        value={fieldType}
                        onChange={(e) => {
                            console.log("Field type selected:", e.target.value);
                            setFieldType(e.target.value);
                        }}
                        fullWidth
                        sx={{ 
                            minWidth: { xs: '100%', sm: '150px' },
                            flex: { sm: 1 }
                        }}
                    >
                        {fieldTypes
                            .filter((type) => type.type !== 'Custom Note') // Exclude Custom Note from options
                            .map((type) => (
                                <MenuItem key={type.type} value={type.type}>
                                    {type.type}
                                </MenuItem>
                            ))}
                    </TextField>
                    <TextField
                        label="Field Name"
                        value={fieldName}
                        onChange={(e) => {
                            console.log("Field name updated to:", e.target.value);
                            setFieldName(e.target.value);
                        }}
                        fullWidth
                        sx={{ 
                            minWidth: { xs: '100%', sm: '150px' },
                            flex: { sm: 1 }
                        }}
                    />
                    <Button 
                        variant="contained" 
                        color="primary" 
                        onClick={handleAddField}
                        fullWidth={{ xs: true, sm: false }}
                        sx={{ minWidth: { sm: '120px' } }}
                    >
                        Add Field
                    </Button>
                </Box>
            )}
            <Droppable 
                droppableId={`custom-note-droppable-${id}`}
                type="CUSTOM_NOTE_FIELD"
            >
                {(provided) => (
                    <Box
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                        sx={{
                            listStyleType: 'none',
                            padding: { xs: 0.5, sm: 1 },
                            minHeight: mode === 'design' ? '50px' : '0px',
                            maxHeight: { xs: '250px', sm: '300px' },
                            overflowY: 'auto',
                            overflowX: 'hidden',
                            border: mode === 'design' ? '1px dashed #CCC' : 'none',
                            width: '100%',
                            boxSizing: 'border-box'
                        }}
                    >
                        {fields.map((field, index) => (
                            <Draggable key={field.id} draggableId={field.id} index={index}>
                                {(provided) => (
                                    <Box
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        {...provided.dragHandleProps}
                                        onMouseDown={handleBeforeDragStart}
                                        onMouseUp={handleAfterDragEnd}
                                        sx={{
                                            padding: { xs: '6px', sm: '8px' },
                                            margin: '4px 0',
                                            backgroundColor: mode === 'design' ? '#eeeeeeff' : 'transparent',
                                            borderRadius: '4px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: { xs: 0.5, sm: 1 },
                                            width: '100%',
                                            boxSizing: 'border-box',
                                            ...provided.draggableProps.style,
                                        }}
                                    >
                                        <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
                                            {fieldSelector(field, fields, setFields, get_item_by_id, get_item_index_by_id, () => {}, mode)}
                                        </Box>
                                        {mode === 'design' && (
                                            <Button
                                                onClick={() => handleRemoveField(field.id)}
                                                variant="contained"
                                                color="error"
                                                size="small"
                                                sx={{
                                                    minWidth: { xs: '32px', sm: '40px' },
                                                    padding: { xs: '4px', sm: '4px 8px' },
                                                    flexShrink: 0
                                                }}
                                            >
                                                X
                                            </Button>
                                        )}
                                    </Box>
                                )}
                            </Draggable>
                        ))}
                        {provided.placeholder}
                    </Box>
                )}
            </Droppable>
        </Box>
    );
};

export default CustomNoteComponent;
